
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { ContactFormFields } from './ContactFormFields';
import { ContactFormData, contactFormSchema } from '@/schemas/ContactFormSchema';
import SuccessMessage from './SuccessMessage';
import { csrfService } from '@/services/csrfService';
import { supabase } from '@/integrations/supabase/client';
import { countries } from '@/utils/countries';

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [messageLength, setMessageLength] = useState(0);
  const maxMessageLength = 200;
  const { toast } = useToast();

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: '',
      lastName: '',
      email: '',
      country: '',
      whatsapp: '',
      language: '',
      message: '',
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    console.log('ContactForm: Starting form submission');

    try {
      // Get user session ID for CSRF token binding
      const userSessionId = await csrfService.getUserSessionId();
      
      // Generate CSRF token
      const csrfToken = await csrfService.generateToken(userSessionId);
      
      if (!csrfToken) {
        throw new Error('Failed to generate security token');
      }

      console.log('ContactForm: CSRF token generated, submitting form');

      // Find the full country name from the country code
      const selectedCountry = countries.find(country => country.code === data.country);
      const countryName = selectedCountry ? selectedCountry.name : data.country;

      // Submit form with CSRF token
      const { data: result, error } = await supabase.functions.invoke('contact-form', {
        body: {
          name: data.name,
          lastname: data.lastName,
          email: data.email,
          country: countryName,
          whatsapp: data.whatsapp,
          language: data.language,
          message: data.message,
        },
        headers: {
          'X-CSRF-Token': csrfToken,
        },
      });

      if (error) {
        console.error('ContactForm: Submission error:', error);
        throw error;
      }

      console.log('ContactForm: Submission successful:', result);
      setIsSuccess(true);
      form.reset();
      
      toast({
        title: "Message sent successfully!",
        description: "Thank you for contacting us. We'll get back to you soon.",
      });

    } catch (error) {
      console.error('ContactForm: Form submission failed:', error);
      
      toast({
        title: "Submission failed",
        description: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return <SuccessMessage />;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <ContactFormFields 
          form={form}
          messageLength={messageLength}
          maxMessageLength={maxMessageLength}
          onMessageChange={setMessageLength}
        />
        
        <Button 
          type="submit" 
          className="w-full bg-finance-600 hover:bg-finance-700 text-white font-semibold py-3 rounded-lg transition-colors"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </Button>
      </form>
    </Form>
  );
};

export default ContactForm;
